import { useCallback, useEffect, useState } from 'react';
import { crmApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { usePermissions } from '@/hooks/usePermissions';
import type { CrmTask } from '@/types/crm';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function CrmTasksPage() {
  const { hasAny } = usePermissions();
  const canManage = hasAny('CRM_MANAGE', 'CRM_CONVERT');
  const [tasks, setTasks] = useState<CrmTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const { rows: paginatedTasks, tablePaginationProps: tasksPaginationProps } = useClientPaginatedRows(tasks);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTasks(await crmApi.listOpenTasks());
    } catch (e) {
      showApiError(e, 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const createTask = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await crmApi.createTask({ title: title.trim(), priority: 'MEDIUM' });
      notify.success('Task created');
      setTitle('');
      await load();
    } catch (e) {
      showApiError(e, 'Failed to create task');
    } finally {
      setSaving(false);
    }
  };

  const complete = async (id: number) => {
    try {
      await crmApi.completeTask(id);
      notify.success('Task completed');
      await load();
    } catch (e) {
      showApiError(e, 'Failed to complete task');
    }
  };

  return (
    <div>
      <PageHeader
        title="Tasks"
        description="Open CRM tasks and follow-ups for your team"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'CRM', to: '/crm' }, { label: 'Tasks' }]}
      />

      {canManage && (
        <div className="card p-4 mb-4 flex flex-wrap gap-2 items-end">
          <Input label="New task" value={title} onChange={(e) => setTitle(e.target.value)} className="min-w-[240px]" />
          <Button onClick={() => void createTask()} loading={saving}>
            Add task
          </Button>
        </div>
      )}

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="p-3">Title</th>
              <th className="p-3">Priority</th>
              <th className="p-3">Due</th>
              <th className="p-3">Status</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="p-6 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            ) : tasks.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-6 text-center text-slate-500">
                  No open tasks
                </td>
              </tr>
            ) : (
              paginatedTasks.map((t) => (
                <tr key={t.id} className="border-t">
                  <td className="p-3 font-medium">{t.title}</td>
                  <td className="p-3">{t.priority ?? '—'}</td>
                  <td className="p-3">{t.dueDate ? t.dueDate.slice(0, 10) : '—'}</td>
                  <td className="p-3">{t.taskStatus}</td>
                  <td className="p-3 text-right">
                    {canManage && t.taskStatus !== 'COMPLETED' && (
                      <button type="button" className="text-primary-600 text-xs hover:underline" onClick={() => void complete(t.id)}>
                        Complete
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <TablePagination {...tasksPaginationProps} hasRows={tasks.length > 0} loading={loading} />
      </div>
    </div>
  );
}
